import { htmlAttr } from '../../../utils/htmlAttr';

// A PR opened by a Cursor background agent has two pieces of raw HTML in its body: the prose the
// agent wrote is fenced off in comment markers, and a footer of "Open in Web" / "Open in Cursor"
// links is appended as a <div> of <a><picture><img> markup. MarkdownContent deliberately does not
// render raw HTML — a PR body is author-supplied text — so both of those reach the reader as
// literal markup. Rather than relax that, we recognise the two shapes here and hand the renderer
// structure instead of HTML: the agent's prose gets quoted so anything a human appended stands
// apart from it, and the footer becomes ordinary links.

export const AGENT_BODY_BEGIN_MARKER = 'CURSOR_AGENT_PR_BODY_BEGIN';
export const AGENT_BODY_END_MARKER = 'CURSOR_AGENT_PR_BODY_END';

// A BEGIN with no END still tells us who wrote everything after it, so that case runs to the end
// of the body rather than falling back to showing the marker.
const AGENT_BODY_SOURCE =
  '<!--\\s*' + AGENT_BODY_BEGIN_MARKER + '\\s*-->([\\s\\S]*?)' +
  '(?:<!--\\s*' + AGENT_BODY_END_MARKER + '\\s*-->|$)';
const HTML_DIV_SOURCE = '<div\\b[^>]*>([\\s\\S]*?)</div>';

const BLOCK = new RegExp(AGENT_BODY_SOURCE + '|' + HTML_DIV_SOURCE, 'gi');
const ANCHOR = /(<a\b[^>]*>)([\s\S]*?)<\/a>/gi;
const HTML_TAG = /<[^>]*>/g;
// What a link row is allowed to contain besides the links themselves.
const LINK_ROW_FILLER = /&nbsp;|\s/g;
const HTTP_URL = /^https?:\/\//i;

export interface PrBodyLink {
  href: string;
  label: string;
}

export type PrBodyBlock =
  | { kind: 'markdown'; text: string }
  | { kind: 'agentBody'; text: string }
  | { kind: 'links'; links: PrBodyLink[] };

/** Turns a <div> of nothing but links into a link block, or undefined if it is anything else. */
function parseLinkRow(content: string): PrBodyBlock | undefined {
  const links: PrBodyLink[] = [];
  ANCHOR.lastIndex = 0;
  let anchor: RegExpExecArray | null;
  while ((anchor = ANCHOR.exec(content)) !== null) {
    const href = htmlAttr(anchor[1], 'href');
    // The body is author-supplied, so only schemes that are safe to hand to the browser.
    if (!href || !HTTP_URL.test(href)) return undefined;
    const inner = anchor[2];
    // Cursor's footer links wrap an <img alt="Open in Web">; a plain <a> has its text instead.
    const label = htmlAttr(inner, 'alt') || inner.replace(HTML_TAG, '').trim();
    links.push({ href, label: label || href });
  }
  if (links.length === 0) return undefined;
  // Anything left over means this div is not a plain row of links, and we would only be guessing
  // at how the rest was meant to look. Leave it to be shown as source.
  if (content.replace(ANCHOR, '').replace(LINK_ROW_FILLER, '') !== '') return undefined;
  return { kind: 'links', links };
}

/** Splits a GitHub PR body into the pieces the description panel renders differently. */
export function parsePrBody(body: string): PrBodyBlock[] {
  const blocks: PrBodyBlock[] = [];
  const pushMarkdown = (text: string) => {
    const trimmed = text.trim();
    if (trimmed !== '') blocks.push({ kind: 'markdown', text: trimmed });
  };

  let consumed = 0;
  BLOCK.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BLOCK.exec(body)) !== null) {
    const [whole, agentBody, divContent] = match;
    const block =
      agentBody !== undefined ? { kind: 'agentBody' as const, text: agentBody.trim() } : parseLinkRow(divContent);
    // A div we did not recognise stays where it is and is shown as source, as before.
    if (!block) continue;
    pushMarkdown(body.slice(consumed, match.index));
    // Markers around nothing are still consumed — showing them as source helps nobody.
    if (block.kind !== 'agentBody' || block.text !== '') blocks.push(block);
    consumed = match.index + whole.length;
  }
  pushMarkdown(body.slice(consumed));
  return blocks;
}
